import { z } from "zod";
import { paginationMetaSchema } from "./base-api-response";
import { ServiceCategory } from "./earnings-setup";
import { DEFAULT_PAGE, DEFAULT_PAGE_SIZE } from "../constants/api.constants";
import { partnerSchema } from "./partner";
import { monthSchema, yearSchema, dateYYYYMMDDSchema } from "./common";
import { paginationSchema } from "./pagination";

/**
 * Wallet Data Object Schema
 */
export const walletDoSchema = z.object({
    id: z.string(),
    partner: z.object({
        id: z.string(),
        name: z.string(),
        phoneNumber: z.string().optional(),
        email: z.string().optional(),
    }),
    // Balance
    currentBalance: z.number(),
    totalCredits: z.number(),
    totalDebits: z.number(),
    // Last transaction tracking
    lastTransactionId: z.string().optional(),
    lastTransactionAt: z.number().optional(),
    // Audit
    createdAt: z.number(),
    updatedAt: z.number(),
    createdBy: z.string(),
    updatedBy: z.string(),
});

export type WalletDo = z.infer<typeof walletDoSchema>;

export const adminWalletListingQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(DEFAULT_PAGE),
    limit: z.coerce.number().int().min(1).max(100).default(DEFAULT_PAGE_SIZE),
    query: z.string().trim().min(1).optional(),
    serviceCategory: z.nativeEnum(ServiceCategory).optional(),
});

export type AdminWalletListingQueryDto = z.infer<typeof adminWalletListingQuerySchema>;

export const adminWalletListingItemSchema = z.object({
    partner: partnerSchema,
    wallet: walletDoSchema,
    pendingApprovalCount: z.number(),
    pendingPaymentCount: z.number(),
});

export type AdminWalletListingItemDto = z.infer<typeof adminWalletListingItemSchema>;

export const adminWalletListingResponseSchema = z.object({
    items: z.array(adminWalletListingItemSchema),
    meta: paginationMetaSchema,
});

export type IAdminWalletListingResponse = z.infer<typeof adminWalletListingResponseSchema>;

/**
 * Partner Payout Status Enum
 */
export enum PartnerPayoutStatus {
    /** Initial state when payout is created */
    REQUESTED = "REQUESTED",
    /** Payout has been calculated */
    CALCULATED = "CALCULATED",
    /** Payout has been approved and is ready for payment */
    APPROVED = "APPROVED",
    /** Payout was rejected during approval */
    REJECTED = "REJECTED",
    /** Payment has been completed */
    PAID = "PAID",
    /** Payout was cancelled */
    CANCELLED = "CANCELLED",
}

/**
 * Query schema for getting payouts for a partner
 */
export const getPayoutsQuerySchema = z.object({
    page: z.coerce.number().int().min(1).default(DEFAULT_PAGE),
    limit: z.coerce.number().int().min(1).max(100).default(DEFAULT_PAGE_SIZE),
    status: z.nativeEnum(PartnerPayoutStatus).optional(),
    isPaid: z
        .string()
        .optional()
        .transform((val) => (val ? val === "true" : undefined)),
    startsAt: dateYYYYMMDDSchema.optional(),
    endsAt: dateYYYYMMDDSchema.optional(),
});

export type GetPayoutsQueryDto = z.infer<typeof getPayoutsQuerySchema>;

/**
 * Partner Payout Summary Schema (for list views)
 */
export const partnerPayoutSummaryResponseSchema = z.object({
    id: z.string().uuid(),
    partner: partnerSchema,
    amount: z.number(),
    description: z.string(),
    transactionId: z.string().uuid().nullish(),
    status: z.nativeEnum(PartnerPayoutStatus),
    isPaid: z.boolean(),
    paidAt: z.number().nullish(),
    paymentReference: z.string().nullish(),
    paymentMethod: z.string().nullish(),
    createdAt: z.number(),
    updatedAt: z.number(),
    createdBy: z.string(),
    updatedBy: z.string(),
});

export type PayoutSummaryResponseDto = z.infer<typeof partnerPayoutSummaryResponseSchema>;

/**
 * Partner Payout Details Schema (for detail views)
 */
export const partnerPayoutDetailsResponseSchema = z.object({
    id: z.string().uuid(),
    partner: partnerSchema,
    amount: z.number(),
    description: z.string(),
    transactionId: z.string().uuid(),
    status: z.nativeEnum(PartnerPayoutStatus),
    isPaid: z.boolean(),
    paidAt: z.number().nullish(),
    paymentReference: z.string().nullish(),
    paymentMethod: z.string().nullish(),
    paymentNotes: z.string().nullish(),
    createdAt: z.number(),
    updatedAt: z.number(),
    createdBy: z.string(),
    updatedBy: z.string(),
});

export type PayoutDetailsResponseDto = z.infer<typeof partnerPayoutDetailsResponseSchema>;

/**
 * Approve Payout Request Schema
 */
export const approvePayoutRequestSchema = z.object({
    notes: z.string().optional(),
});

export type ApprovePayoutRequestDto = z.infer<typeof approvePayoutRequestSchema>;

/**
 * Reject Payout Request Schema
 */
export const rejectPayoutRequestSchema = z.object({
    rejectionReason: z.string().min(10, "Rejection reason must be at least 10 characters"),
});

export type RejectPayoutRequestDto = z.infer<typeof rejectPayoutRequestSchema>;

/**
 * Mark Paid Request Schema
 */
export const markPaidRequestSchema = z.object({
    paymentReference: z.string().min(1, "Payment reference is required").nullish(),
    paymentMethod: z.string().nullish(),
    paymentNotes: z.string().nullish(),
});

export type MarkPaidRequestDto = z.infer<typeof markPaidRequestSchema>;

/**
 * Payouts List Response Schema
 */
export const payoutsListResponseSchema = z.object({
    items: z.array(partnerPayoutSummaryResponseSchema),
    meta: paginationMetaSchema,
});

export type IPayoutsListResponse = z.infer<typeof payoutsListResponseSchema>;

/**
 * Transaction Type Enum
 */
export enum TransactionType {
    CREDIT = "CREDIT",
    DEBIT = "DEBIT",
}

/**
 * Transaction Source Type Enum
 */
export enum TransactionSourceType {
    // Credits
    WEEKLY_EARNING = "WEEKLY_EARNING",
    BONUS = "BONUS",
    REFERRAL_BONUS = "REFERRAL_BONUS",
    INCENTIVE = "INCENTIVE",
    REFUND = "REFUND",
    ADJUSTMENT_CREDIT = "ADJUSTMENT_CREDIT",
    // Debits
    PAYOUT = "PAYOUT",
    LOAN = "LOAN",
    LOAN_REPAYMENT = "LOAN_REPAYMENT",
    PENALTY = "PENALTY",
    DEDUCTION = "DEDUCTION",
    ADJUSTMENT_DEBIT = "ADJUSTMENT_DEBIT",
}

/**
 * Created By Schema (simple string for now)
 */
const createdBySchema = z.string();
const updatedBySchema = z.string();

/**
 * Transaction History Query Schema
 */
export const transactionHistoryQuerySchema = paginationSchema.extend({
    type: z.nativeEnum(TransactionType).optional(),
    sourceType: z.nativeEnum(TransactionSourceType).optional(),
    fromDate: z.coerce.number().int().positive().optional(),
    toDate: z.coerce.number().int().positive().optional(),
    month: monthSchema(),
    year: yearSchema(),
});

export type TransactionHistoryQueryDto = z.infer<typeof transactionHistoryQuerySchema>;

/**
 * Wallet Transaction Response Schema
 */
export const walletTransactionResponseSchema = z.object({
    id: z.string().uuid(),
    transactionCode: z.string(),
    transactionType: z.nativeEnum(TransactionType),
    amount: z.number(),
    balanceBefore: z.number(),
    balanceAfter: z.number(),
    sourceType: z.nativeEnum(TransactionSourceType),
    sourceReferenceId: z.string().uuid("Invalid source reference ID format").optional(),
    description: z.string(),
    processedAt: z.number(),
    createdAt: z.number(),
    createdBy: createdBySchema,
    updatedBy: updatedBySchema,
});

export type WalletTransactionResponseDto = z.infer<typeof walletTransactionResponseSchema>;

/**
 * Transaction History List Response Schema
 */
export const transactionHistoryListResponseSchema = z.object({
    items: z.array(walletTransactionResponseSchema),
    meta: paginationMetaSchema,
});

export type ITransactionHistoryListResponse = z.infer<typeof transactionHistoryListResponseSchema>;

/**
 * Wallet Balance Response Schema
 */
export const walletBalanceResponseSchema = z.object({
    id: z.string(),
    partner: partnerSchema,
    currentBalance: z.number(),
    totalCredits: z.number(),
    totalDebits: z.number(),
    lastTransactionAt: z.number().optional(),
    updatedAt: z.number(),
});

export type WalletBalanceResponseDto = z.infer<typeof walletBalanceResponseSchema>;

/**
 * Trigger Weekly Payout Request - query params and response
 */
export interface ITriggerWeeklyPayoutRequestParams {
    overridePendingPaymentsCheck?: boolean;
}

export interface ITriggerWeeklyPayoutRequestResponse {
    status: string;
    jobId: string;
    minBalanceThreshold: number;
}

